import { Routes } from '@angular/router';
import { paymentDataGuard } from 'shared/src/guards';
import { authGuard } from './guards/auth.guard';

// Every route is lazy: the initial bundle carries the shell (header, footer, audio panel) and
// nothing else. Checkout in particular drags in Stripe Elements and the Rive-animated modals,
// which no first paint on any route needs.
export const routes: Routes = [
  // The library IS the app's home. No auth guard: the component itself shows a sign-in
  // prompt to signed-out visitors instead of bouncing them to /authentication/login.
  {
    path: '',
    loadComponent: () =>
      import('./features/library/library.component').then(m => m.LibraryComponent),
  },
  { path: 'library', redirectTo: '', pathMatch: 'full' },
  // Guarded (unlike the library): a deep link to an item is meaningless signed out, and the
  // guard's returnUrl brings the visitor back here after login.
  {
    path: 'item/:id',
    loadComponent: () => import('./features/item/item.component').then(m => m.ItemComponent),
    canActivate: [authGuard],
  },
  {
    path: 'user-discount',
    loadComponent: () =>
      import('./features/paywalls/existing-user-discount-paywall/existing-user-discount-paywall.component').then(
        m => m.ExistingUserDiscountPaywallComponent
      ),
  },
  {
    path: 'holiday-discount',
    loadComponent: () =>
      import('./features/paywalls/holiday-discount-paywall/holiday-discount-paywall.component').then(
        m => m.HolidayDiscountPaywallComponent
      ),
  },
  {
    path: 'checkout',
    loadComponent: () =>
      import('shared/src/components/payment/payment.component').then(m => m.PaymentComponent),
    canActivate: [paymentDataGuard],
  },
  {
    path: 'payment-result',
    loadComponent: () =>
      import('shared/src/components/post-payment-flow/post-payment-flow.component').then(
        m => m.PostPaymentFlowComponent
      ),
    data: { checkoutUrl: '/user-discount' },
  },
  {
    path: 'account',
    loadComponent: () =>
      import('./features/account/profile.component').then(m => m.ProfileComponent),
    canActivate: [authGuard],
  },
  {
    path: 'recently-deleted',
    loadComponent: () =>
      import('./features/recently-deleted/recently-deleted.component').then(
        m => m.RecentlyDeletedComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'subscription-management',
    loadComponent: () =>
      import('./features/subscription/subscription-management.component').then(
        m => m.SubscriptionManagementComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'subscription-cancellation',
    loadComponent: () =>
      import('./features/subscription/cancellation-flow/cancellation-flow.component').then(
        m => m.CancellationFlowComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'error-500',
    loadComponent: () =>
      import('./components/common/internal-error/internal-error.component').then(
        m => m.InternalErrorComponent
      ),
  },

  {
    path: 'authentication/login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'authentication/logout',
    loadComponent: () =>
      import('./features/auth/logout/logout.component').then(m => m.LogoutComponent),
  },
  {
    path: 'authentication/email',
    loadComponent: () =>
      import('./features/auth/email-login/email-login.component').then(m => m.EmailLoginComponent),
  },
  {
    path: 'authentication/link',
    loadComponent: () =>
      import('./features/auth/link-login/link-login.component').then(m => m.LinkLoginComponent),
  },
  {
    path: 'authentication/link-info',
    loadComponent: () =>
      import('./features/auth/login-link-info/login-link-info.component').then(
        m => m.LoginLinkInfoComponent
      ),
  },

  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then(m => m.LoginComponent),
  },
  // Chrome extension login handshake target — the published extension matches the '/success' URL
  // and reads the token from its query params (login/link-login navigate here with
  // ?destination=peech-chrome-extension&token=<refreshToken>). Do NOT rename this path.
  {
    path: 'success',
    loadComponent: () =>
      import('./features/extension-login-success/extension-login-success.component').then(
        m => m.ExtensionLoginSuccessComponent
      ),
  },

  {
    path: 'credits',
    loadComponent: () =>
      import('./features/credits/credits-paywall.component').then(m => m.CreditsPaywallComponent),
  },
  {
    path: 'credits/success',
    loadComponent: () =>
      import('./features/credits/credits-success/credits-success.component').then(
        m => m.CreditsSuccessComponent
      ),
  },

  {
    path: '**',
    loadComponent: () =>
      import('./components/common/not-found/not-found.component').then(m => m.NotFoundComponent),
  }, // This line will remain down from the whole pages component list
];
